/**
 * 重音字符模糊匹配工具
 *
 * 用于前端筛选器的"特殊字符模糊匹配"场景：
 * - 用户输入纯 ASCII 关键字（如 `pokemon`）时，应能命中带变音字符的内容（如 `Pokémon`）；
 * - 反之，输入 `Pokémon` 时也应能命中原文。
 *
 * 实现参照后端 accentMap 方案的"方案 A：客户端规范化（推荐）"：
 * 把字符串先反归一化为基础字符再做小写比较，从而在不上正则的前提下完成
 * 双向不敏感匹配，避免运行时构造正则带来的注入与性能风险。
 *
 * 当前覆盖：拉丁、北欧、东欧、土耳其、越南借字常见变体；
 * 不覆盖：多对一映射（如 ß→ss、æ→ae、œ→oe），如有需要可在 accentMap 内补充
 * 或在调用前自行替换。
 */

// key: 基础字符（小写）
// value: 该字符的所有重音变体（含大小写）
export const accentMap: Record<string, string[]> = {
  // 元音
  a: ['á', 'à', 'â', 'ä', 'ã', 'å', 'ā', 'Á', 'À', 'Â', 'Ä', 'Ã', 'Å', 'Ā'],
  e: ['é', 'è', 'ê', 'ë', 'ē', 'É', 'È', 'Ê', 'Ë', 'Ē'],
  i: ['í', 'ì', 'î', 'ï', 'ī', 'Í', 'Ì', 'Î', 'Ï', 'Ī'],
  o: ['ó', 'ò', 'ô', 'ö', 'õ', 'ø', 'ō', 'Ó', 'Ò', 'Ô', 'Ö', 'Õ', 'Ø', 'Ō'],
  u: ['ú', 'ù', 'û', 'ü', 'ū', 'Ú', 'Ù', 'Û', 'Ü', 'Ū'],
  y: ['ý', 'ÿ', 'Ý', 'Ÿ'],
  // 辅音
  c: ['ć', 'č', 'ç', 'Ć', 'Č', 'Ç'],
  n: ['ñ', 'ń', 'ň', 'Ñ', 'Ń', 'Ň'],
  s: ['ś', 'š', 'ş', 'Ś', 'Š', 'Ş'],
  z: ['ź', 'ž', 'ż', 'Ź', 'Ž', 'Ż'],
  r: ['ř', 'Ř'],
  t: ['ť', 'Ť'],
  d: ['ď', 'đ', 'Ď', 'Đ'],
  g: ['ğ', 'Ğ'],
  l: ['ł', 'Ł'],
};

// 反向映射：重音字符 → 基础字符（模块加载时构建一次）
const reverseAccentMap: Record<string, string> = (() => {
  const map: Record<string, string> = {};
  for (const base of Object.keys(accentMap)) {
    for (const accent of accentMap[base]) {
      map[accent] = base;
    }
  }
  return map;
})();

/**
 * 将字符串中的重音字符反归一化为基础字符
 *
 * @example
 * normalizeAccents('Pokémon') // => 'Pokemon'
 * normalizeAccents('Côte d\'Ivoire') // => 'Cote d\'Ivoire'
 */
export function normalizeAccents(s: string): string {
  if (!s) return s ?? '';
  // 单次遍历避免多次扫描；对未命中的字符原样保留
  let result = '';
  for (const ch of s) {
    result += reverseAccentMap[ch] ?? ch;
  }
  return result;
}

/**
 * 反归一化 + 转小写，常用于"忽略大小写 + 忽略重音"的搜索比较
 *
 * @example
 * normalizeAccentsLower('Pokémon') // => 'pokemon'
 */
export function normalizeAccentsLower(s: string): string {
  return normalizeAccents(s ?? '').toLowerCase();
}

/**
 * 判断字符串是否含任何已登记的重音字符
 *
 * 用途：调用方可据此决定走"精确匹配"还是"模糊匹配"分支。
 */
export function containsAccentChars(s: string): boolean {
  if (!s) return false;
  for (const ch of s) {
    if (reverseAccentMap[ch] !== undefined) {
      return true;
    }
  }
  return false;
}

/**
 * 大小写 + 重音字符均不敏感的 includes
 *
 * - haystack 与 needle 任一为 falsy 时返回 false；
 * - needle 为空字符串视为 true（与原生 String.includes 行为一致）。
 *
 * @example
 * accentIncludes('Pokémon UNITE', 'pokemon') // => true
 * accentIncludes('Côte d\'Ivoire', 'cote')   // => true
 */
export function accentIncludes(
  haystack: string | null | undefined,
  needle: string | null | undefined,
): boolean {
  if (haystack == null) return false;
  if (needle == null) return false;
  if (needle === '') return true;
  return normalizeAccentsLower(haystack).includes(normalizeAccentsLower(needle));
}

/**
 * 大小写 + 重音字符均不敏感的 indexOf
 *
 * 主要给排序打分使用（如"命中位置越靠前权重越高"）。
 * 未命中或入参非法时返回 -1。
 */
export function accentIndexOf(
  haystack: string | null | undefined,
  needle: string | null | undefined,
): number {
  if (haystack == null || needle == null) return -1;
  if (needle === '') return 0;
  return normalizeAccentsLower(haystack).indexOf(normalizeAccentsLower(needle));
}

/** 任一字段命中关键词即返回 true；关键词为空视为全部命中 */
export function accentInsensitiveMatch(
  keyword: string,
  ...fields: (string | null | undefined)[]
): boolean {
  const needle = keyword?.trim() ?? '';
  if (!needle) {
    return true;
  }
  return fields.some(field => accentIncludes(field, needle));
}
